import i18n from '@/utils/i18n';
import PencilIcon from '@assets/icons/pencil.svg';
import React, { useMemo, useState } from 'react';
import { ActionSheetContent } from '@/components/action-sheet';
import { ActionSheetKeyboardAwareView } from './action-sheet-keyboard-aware-view';
import { AddNewFooter } from '@/components/add-new-footer';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import {
  EditOrderGuideActions,
  Partition,
} from '@/hooks/use-edit-order-guide-reducer';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { ListItemSeparator, ListOption } from '@/components/list-item';
import { OrderGuideActionSheetNameInput } from './order-guide-action-sheet-name-input';
import {
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { SortableList } from '@/components/sortable-list';
import { useEditOrderGuideActions } from '@/contexts/edit-order-guide-context';
import { useTheme } from '@/contexts/theme-context';
import { validatePartitionName } from '@/utils/order-guide';

interface Props {
  onClose: () => void;
  partitions: Partition[];
}

enum EditPartitionSheetRoute {
  Base = 'BASE',
  Create = 'CREATE',
  Delete = 'DELETE',
  Update = 'UPDATE',
}

export const EditPartitionsActionSheet: React.FC<Props> = ({
  onClose,
  partitions,
}) => {
  const dispatch = useEditOrderGuideActions();
  const theme = useTheme();
  const windowDimensions = useWindowDimensions();

  const [inputError, setInputError] = useState('');

  const [partitionToEdit, setPartitionToEdit] = useState<ListOption | null>(
    null,
  );
  const [listItems, defaultPartitionUUID] = useMemo<
    [ListOption[], string | null]
  >(() => {
    let defaultUUID = null;
    const partitionList = partitions.reduce<ListOption[]>((acc, partition) => {
      if (!partition.isDefault) {
        acc.push({
          title: partition.partitionName!,
          value: partition.partitionUUID,
        });
      } else {
        defaultUUID = partition.partitionUUID;
      }
      return acc;
    }, []);
    return [partitionList, defaultUUID];
  }, [partitions]);

  const actionSheetMaxHeight = Math.floor(windowDimensions.height * 0.85);

  return (
    <CombinedActionSheet
      contentStyle={[styles.paddingReset, { maxHeight: actionSheetMaxHeight }]}
      hasStickyHeader={false}
      initialRoute={EditPartitionSheetRoute.Base}
      onClose={onClose}
      onRouteChange={() => setInputError('')}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case EditPartitionSheetRoute.Base:
            return (
              <GestureHandlerRootView style={styles.containerFix}>
                <SortableList<ListOption>
                  containerStyle={styles.containerFix}
                  dragHandleSide="left"
                  items={listItems}
                  ListFooterComponent={() => (
                    <>
                      <ListItemSeparator style={styles.newFooterSeparator} />
                      <AddNewFooter
                        listItemStyle={styles.newFooter}
                        onSelect={() => {
                          jumpTo(EditPartitionSheetRoute.Create);
                        }}
                        size="small"
                        title={i18n.t('new_group')}
                      />
                    </>
                  )}
                  onChange={(items) => {
                    dispatch({
                      payload: {
                        sortedPartitionUUIDs: [
                          ...items.map((item) => String(item.value)),
                          defaultPartitionUUID!,
                        ],
                      },
                      type: EditOrderGuideActions.SORT_PARTITIONS,
                    });
                  }}
                  renderInPortal={false}
                  renderItemContent={(item) => (
                    <TouchableOpacity
                      onPress={() => {
                        setPartitionToEdit(item);
                        jumpTo(EditPartitionSheetRoute.Update);
                      }}
                      style={styles.itemTouchable}
                    >
                      <Text
                        numberOfLines={1}
                        style={[TYPOGRAPHY.listTitle, styles.listItemTitle]}
                      >
                        {item.title}
                      </Text>
                      <View style={styles.rightAction}>
                        <PencilIcon stroke={theme.colors.inactiveFill} />
                      </View>
                    </TouchableOpacity>
                  )}
                  scrollViewProps={{
                    alwaysBounceVertical: false,
                  }}
                />
              </GestureHandlerRootView>
            );
          case EditPartitionSheetRoute.Create:
            return (
              <ActionSheetKeyboardAwareView style={styles.crudContainer}>
                <OrderGuideActionSheetNameInput
                  inputError={inputError}
                  nameInputLabel={i18n.t('group_name')}
                  onChangeText={() => {
                    setInputError('');
                  }}
                  onSave={(name: string) => {
                    dispatch((actionDispatch, getState) => {
                      const partitionNames = getState().partitions.reduce<
                        string[]
                      >((acc, p) => {
                        if (p.partitionName) {
                          acc.push(p.partitionName);
                        }
                        return acc;
                      }, []);
                      if (!validatePartitionName(name, partitionNames)) {
                        setInputError(i18n.t('group_name_error'));
                      } else {
                        actionDispatch({
                          payload: {
                            partitionName: name,
                          },
                          type: EditOrderGuideActions.ADD_PARTITION,
                        });
                        jumpTo(EditPartitionSheetRoute.Base);
                      }
                    });
                  }}
                  saveButtonText={i18n.t('create')}
                />
              </ActionSheetKeyboardAwareView>
            );
          case EditPartitionSheetRoute.Update:
            return (
              <ActionSheetKeyboardAwareView style={styles.crudContainer}>
                <OrderGuideActionSheetNameInput
                  deleteButtonProps={{
                    children: i18n.t('delete_group'),
                    onPress: () => {
                      jumpTo(EditPartitionSheetRoute.Delete);
                    },
                    trackingData: {
                      name: 'edit_partition_sheet.delete_button',
                      properties: {
                        partitionUUID: partitionToEdit?.value,
                      },
                    },
                  }}
                  initialName={partitionToEdit?.title}
                  inputError={inputError}
                  nameInputLabel={i18n.t('group_name')}
                  onChangeText={() => {
                    setInputError('');
                  }}
                  onSave={(name: string) => {
                    dispatch((actionDispatch, getState) => {
                      if (
                        getState().partitions.some(
                          (p) =>
                            p.partitionUUID !== partitionToEdit?.value &&
                            p.partitionName === name,
                        )
                      ) {
                        setInputError(i18n.t('group_name_error'));
                      } else {
                        actionDispatch({
                          payload: {
                            partitionName: name,
                            partitionUUID: String(partitionToEdit?.value),
                          },
                          type: EditOrderGuideActions.SET_PARTITION_NAME,
                        });
                        jumpTo(EditPartitionSheetRoute.Base);
                        setPartitionToEdit(null);
                      }
                    });
                  }}
                />
              </ActionSheetKeyboardAwareView>
            );
          case EditPartitionSheetRoute.Delete:
            return (
              <View style={styles.crudContainer}>
                <ActionSheetContent
                  actions={[i18n.t('delete_group'), i18n.t('cancel')]}
                  message={i18n.t('delete_partition_confirmation.message', {
                    partitionName: partitionToEdit?.title,
                  })}
                  onAction={(_, idx) => {
                    if (idx === 0) {
                      dispatch({
                        payload: {
                          partitionUUID: String(partitionToEdit?.value),
                        },
                        type: EditOrderGuideActions.REMOVE_PARTITION,
                      });
                      jumpTo(EditPartitionSheetRoute.Base);
                      setPartitionToEdit(null);
                    } else {
                      jumpTo(EditPartitionSheetRoute.Update);
                    }
                  }}
                  title={i18n.t('delete_partition_confirmation.title', {
                    partitionName: partitionToEdit?.title,
                  })}
                />
              </View>
            );
          default:
            return null;
        }
      }}
      routes={{
        [EditPartitionSheetRoute.Base]: {
          previousKey: null,
          showSmallLayoutTitle: true,
          title: i18n.t('edit_groups'),
        },
        [EditPartitionSheetRoute.Create]: {
          previousKey: EditPartitionSheetRoute.Base,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: true,
          title: i18n.t('new_group'),
        },
        [EditPartitionSheetRoute.Update]: {
          previousKey: EditPartitionSheetRoute.Base,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: true,
          title: i18n.t('edit_group'),
        },
        [EditPartitionSheetRoute.Delete]: {
          previousKey: EditPartitionSheetRoute.Base,
          showSmallLayoutBackButton: false,
          showSmallLayoutTitle: false,
        },
      }}
      titleStyle={styles.titleStyle}
    />
  );
};

const styles = StyleSheet.create({
  containerFix: {
    flex: -1,
  },
  crudContainer: {
    paddingHorizontal: SCREEN_PADDING,
  },
  itemTouchable: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 16,
  },
  listItemTitle: {
    flexShrink: 1,
  },
  newFooter: {
    marginHorizontal: SCREEN_PADDING,
    paddingHorizontal: 0,
  },
  newFooterSeparator: {
    marginHorizontal: Platform.OS === 'web' ? 0 : SCREEN_PADDING,
  },
  paddingReset: {
    paddingHorizontal: 0,
  },
  rightAction: {
    flexShrink: 0,
    marginLeft: 12,
  },
  titleStyle: {
    paddingLeft: SCREEN_PADDING,
  },
});
